import { DateTime } from 'luxon';
import { ValidationError, type BankStatement } from '@app/domain/entities';

/** PUMB statement datetimes are Ukrainian bank time, same as Monobank instants. */
const PUMB_BANK_TIME_ZONE = 'Europe/Kyiv';

type ParsedBankStatement = Omit<BankStatement, 'appAccountId'>;

type TableCell = string | number | Date | boolean | null | undefined;
type PdfTable = TableCell[][];
/**
 * Narrow runtime shape of the `pdf-parse` PDFParse class that the PUMB importer uses:
 * construct with in-memory PDF data, then call the static worker configuration hook.
 */
type PdfParseClass = {
  new (options: { data: Uint8Array }): {
    getTable: () => Promise<unknown>;
    destroy: () => Promise<void>;
  };
  setWorker: (workerSrc: string) => string;
};
let pdfParseModulePromise: Promise<typeof import('pdf-parse')> | null = null;
const PUMB_PDF_WORKER_ASSET_PATH = '/assets/pdf.worker.mjs';

const HEADER_ALIASES = {
  date: [
    'date',
    'transaction date',
    'operation date',
    'дата',
    'дата операції',
    'дата операции',
    'дата проведення',
    'дата проведения',
    'дата та час операції',
  ],
  description: [
    'description',
    'details',
    'purpose',
    'comment',
    'назначение',
    'призначення',
    'опис',
    'описание',
    'деталі',
    'детали',
    'деталі операції',
  ],
  amount: ['amount', 'sum', 'сума', 'сумма', 'сума у валюті рахунку'],
  debit: ['debit', 'withdrawal', 'expense', 'дебет', 'списання', 'списання', 'витрата', 'расход'],
  credit: ['credit', 'deposit', 'income', 'кредит', 'зарахування', 'зачисление', 'дохід', 'доход'],
  currency: ['currency', 'валюта', 'curr'],
  reference: ['reference', 'document number', 'id', 'референс', 'номер документа', 'номер документу'],
  operationType: ['operation type', 'transaction type', 'тип операції', 'тип транзакції', 'опис операції'],
} as const;

interface HeaderIndexes {
  date: number;
  description: number;
  amount: number | null;
  debit: number | null;
  credit: number | null;
  operationType: number | null;
  currency: number | null;
  reference: number | null;
}

export class PumbFileParserService {
  async parseFile(file: File): Promise<ParsedBankStatement[]> {
    if (!isPdfStatementFile(file)) {
      throw new ValidationError('file', 'The selected file must be a PDF statement');
    }

    return mapPumbPdfTables(await this.loadPdfTables(file));
  }

  protected async loadPdfTables(file: File): Promise<PdfTable[]> {
    const parser = await this.createPdfParser(await this.loadPdfParseClass(), file);

    try {
      return extractPdfTables(await parser.getTable());
    } finally {
      await parser.destroy();
    }
  }

  protected async loadPdfParseClass(): Promise<PdfParseClass> {
    const { PDFParse } = await (pdfParseModulePromise ??= import('pdf-parse'));
    return PDFParse as PdfParseClass;
  }

  protected async createPdfParser(PDFParseClass: PdfParseClass, file: File): Promise<InstanceType<PdfParseClass>> {
    this.configurePdfWorker(PDFParseClass, globalThis.document?.baseURI ?? '');
    return new PDFParseClass({ data: new Uint8Array(await file.arrayBuffer()) });
  }

  private configurePdfWorker(PDFParseClass: Pick<PdfParseClass, 'setWorker'>, baseUri: string): string {
    return configurePumbPdfWorker(PDFParseClass, resolvePumbPdfWorkerSrc(baseUri));
  }
}

export function configurePumbPdfWorker(PDFParseClass: Pick<PdfParseClass, 'setWorker'>, workerSrc: string): string {
  return PDFParseClass.setWorker(workerSrc);
}

export function resolvePumbPdfWorkerSrc(baseUri: string): string {
  return baseUri ? new URL(PUMB_PDF_WORKER_ASSET_PATH, baseUri).toString() : PUMB_PDF_WORKER_ASSET_PATH;
}

export function mapPumbPdfTables(tables: PdfTable[]): ParsedBankStatement[] {
  let lastHeaderIndexes: HeaderIndexes | null = null;

  const parsedRows = tables.flatMap((table) => {
    try {
      const { statements, headerIndexes } = mapNormalizedTableRows(normalizeTable(table), lastHeaderIndexes);
      lastHeaderIndexes = headerIndexes;
      return statements;
    } catch (error) {
      if (error instanceof ValidationError) {
        return [];
      }
      throw error;
    }
  });

  const uniqueRows = [...new Map(parsedRows.map((row) => [row.externalId, row])).values()];
  if (uniqueRows.length === 0) {
    throw new ValidationError('file', 'No importable rows were found in the selected file');
  }

  return uniqueRows;
}

/**
 * Parses already-normalised rows (output of {@link normalizeTable}) into bank statements.
 *
 * When `resolveHeaderRow` cannot find valid column headers in the supplied rows it falls
 * back to `fallbackHeaderIndexes` (header from a previously parsed page) and treats
 * every row as a data row.  This allows multi-page PDFs where continuation pages do not
 * repeat the table header to be processed correctly.
 */
function mapNormalizedTableRows(
  rows: TableCell[][],
  fallbackHeaderIndexes: HeaderIndexes | null,
): { statements: ParsedBankStatement[]; headerIndexes: HeaderIndexes } {
  const nonEmptyRows = rows.filter((row) => row.some((cell) => normalizeCell(cell) !== ''));
  if (nonEmptyRows.length === 0) {
    throw new ValidationError('file', 'The file does not contain any rows');
  }

  let headerIndexes: HeaderIndexes;
  let dataRows: TableCell[][];

  try {
    ({ headerIndexes, dataRows } = resolveHeaderRow(nonEmptyRows));
  } catch (error) {
    if (error instanceof ValidationError && fallbackHeaderIndexes !== null) {
      headerIndexes = fallbackHeaderIndexes;
      dataRows = nonEmptyRows;
    } else {
      throw error;
    }
  }

  const statements: ParsedBankStatement[] = [];
  dataRows.forEach((row, index) => {
    const parsed = mapRow(row, headerIndexes, index + 2);
    if (parsed) {
      statements.push(parsed);
    }
  });

  return { statements, headerIndexes };
}

export function mapPumbTableRows(rows: TableCell[][]): ParsedBankStatement[] {
  const normalizedRows = rows.filter((row) => row.some((cell) => normalizeCell(cell) !== ''));
  if (normalizedRows.length === 0) {
    throw new ValidationError('file', 'The file does not contain any rows');
  }

  const { headerIndexes, dataRows } = resolveHeaderRow(normalizedRows);
  const parsedRows: ParsedBankStatement[] = [];

  dataRows.forEach((row, index) => {
    const parsed = mapRow(row, headerIndexes, index + 2);
    if (parsed) {
      parsedRows.push(parsed);
    }
  });

  if (parsedRows.length === 0) {
    throw new ValidationError('file', 'No importable rows were found in the selected file');
  }

  return parsedRows;
}

function resolveHeaderRow(rows: TableCell[][]): { headerIndexes: HeaderIndexes; dataRows: TableCell[][] } {
  let lastError: ValidationError | null = null;

  for (const [index, row] of rows.entries()) {
    try {
      return {
        headerIndexes: resolveHeaderIndexes(row),
        dataRows: rows.slice(index + 1),
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        lastError = error;
        continue;
      }
      throw error;
    }
  }

  throw lastError ?? new ValidationError('file', 'The selected file is missing required PUMB statement columns');
}

function resolveHeaderIndexes(headerRow: TableCell[]): HeaderIndexes {
  const headers = headerRow.map((cell) => normalizeHeader(normalizeCell(cell)));
  const date = findHeaderIndex(headers, HEADER_ALIASES.date);
  const description = findHeaderIndex(headers, HEADER_ALIASES.description);
  const amount = findHeaderIndex(headers, HEADER_ALIASES.amount);
  const debit = findHeaderIndex(headers, HEADER_ALIASES.debit);
  const credit = findHeaderIndex(headers, HEADER_ALIASES.credit);

  if (date === null || description === null || (amount === null && debit === null && credit === null)) {
    const found = headers.filter((h) => h).join(', ') || '(no headers detected)';
    const missing: string[] = [];
    if (date === null) missing.push('date');
    if (description === null) missing.push('description');
    if (amount === null && debit === null && credit === null) missing.push('amount or debit/credit');

    throw new ValidationError('file', `Missing required PUMB columns: ${missing.join(', ')}. Found headers: ${found}`);
  }

  return {
    date,
    description,
    amount,
    debit,
    credit,
    operationType: findHeaderIndex(headers, HEADER_ALIASES.operationType),
    currency: findHeaderIndex(headers, HEADER_ALIASES.currency),
    reference: findHeaderIndex(headers, HEADER_ALIASES.reference),
  };
}

function mapRow(row: TableCell[], headerIndexes: HeaderIndexes, rowNumber: number): ParsedBankStatement | null {
  const description = normalizeCell(row[headerIndexes.description]);
  if (isSummaryRow(description)) {
    return null;
  }

  const date = parseDateValue(row[headerIndexes.date]);
  if (!date) {
    return null;
  }

  const amount = resolveAmount(row, headerIndexes);
  if (amount === null || amount === 0) {
    return null;
  }

  const currencyCode = resolveCurrencyCode(row, headerIndexes);
  if (!currencyCode) return null;
  const reference = normalizeCell(headerIndexes.reference === null ? null : row[headerIndexes.reference]) || null;
  const normalizedDescription = description || reference || `PUMB row ${rowNumber}`;

  return {
    externalId: buildExternalId({
      date,
      amount,
      currencyCode,
      description: normalizedDescription,
      reference,
    }),
    date,
    amount,
    currencyCode,
    description: normalizedDescription,
    reference,
  };
}

function resolveAmount(row: TableCell[], headerIndexes: HeaderIndexes): number | null {
  if (headerIndexes.amount !== null) {
    const rawAmount = parseAmount(row[headerIndexes.amount]);
    if (rawAmount === null) return null;
    if (headerIndexes.operationType !== null) {
      return applyOperationTypeSign(rawAmount, row[headerIndexes.operationType]);
    }
    return rawAmount;
  }

  const debit = headerIndexes.debit === null ? null : parseAmount(row[headerIndexes.debit]);
  const credit = headerIndexes.credit === null ? null : parseAmount(row[headerIndexes.credit]);

  if (debit && debit !== 0) {
    return -Math.abs(debit);
  }

  if (credit && credit !== 0) {
    return Math.abs(credit);
  }

  return null;
}

const DEBIT_OPERATION_TYPES = new Set([
  'покупка',
  'комісія',
  'нарахування комісії',
  'блокування',
  'зняття готівки',
  'зняття',
  'списання',
  'оплата',
]);

const CREDIT_OPERATION_TYPES = new Set([
  'надходження',
  'повернення',
  'повернення коштів',
  'зарахування',
  'зарахування коштів',
  'нарахування',
  'нарахування відсотків',
  'депозит',
]);

function applyOperationTypeSign(amount: number, operationTypeCell: TableCell): number | null {
  const type = normalizeHeader(normalizeCell(operationTypeCell));
  if (!type) return null;
  if (CREDIT_OPERATION_TYPES.has(type)) return Math.abs(amount);
  if (DEBIT_OPERATION_TYPES.has(type)) return -Math.abs(amount);
  return amount;
}

function resolveCurrencyCode(row: TableCell[], headerIndexes: HeaderIndexes): string | null {
  const fileCurrency = headerIndexes.currency === null ? '' : normalizeCell(row[headerIndexes.currency]).toUpperCase();
  if (fileCurrency) return fileCurrency;

  if (headerIndexes.amount !== null) {
    const amountCell = normalizeCell(row[headerIndexes.amount]);
    const match = amountCell.match(/\b([A-Z]{3})\s*$/);
    if (match) return match[1];
  }

  return null;
}

function buildExternalId(input: {
  date: Date;
  amount: number;
  currencyCode: string;
  description: string;
  reference: string | null;
}): string {
  const ref = input.reference ? normalizeHeader(input.reference) : 'no-ref';
  return [
    input.date.toISOString(),
    input.amount.toFixed(2),
    input.currencyCode,
    normalizeHeader(input.description),
    ref,
  ].join('|');
}

/**
 * Interprets a PUMB wall-clock timestamp as Europe/Kyiv, converts it to UTC,
 * then drops the time of day. That matches Monobank: unix instant → UTC midnight.
 */
function kyivWallTimeToUtcMidnight(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
): Date | null {
  const kyiv = DateTime.fromObject({ year, month, day, hour, minute, second }, { zone: PUMB_BANK_TIME_ZONE });
  if (!kyiv.isValid) {
    return null;
  }

  return kyiv.toUTC().startOf('day').toJSDate();
}

function parseDateValue(value: TableCell): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const excelEpoch = Date.UTC(1899, 11, 30);
    const parsed = new Date(excelEpoch + Math.round(value * 86_400_000));
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return kyivWallTimeToUtcMidnight(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth() + 1,
      parsed.getUTCDate(),
      parsed.getUTCHours(),
      parsed.getUTCMinutes(),
      parsed.getUTCSeconds(),
    );
  }

  const normalized = normalizeCell(value);
  if (!normalized) return null;

  const dotMatch = normalized.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
  if (dotMatch) {
    const [, day, month, year, hour = '0', minute = '0', second = '0'] = dotMatch;
    const fullYear = year.length === 2 ? `20${year}` : year;
    return kyivWallTimeToUtcMidnight(
      Number(fullYear),
      Number(month),
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    );
  }

  // ISO-style datetime strings from PUMB PDFs ("2026-02-27 21:02:16") are Kyiv time.
  const isoDateTimeMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/);
  if (isoDateTimeMatch) {
    const [, year, month, day, hour = '0', minute = '0', second = '0'] = isoDateTimeMatch;
    return kyivWallTimeToUtcMidnight(
      Number(year),
      Number(month),
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    );
  }

  const isoDate = new Date(normalized);
  return Number.isNaN(isoDate.getTime()) ? null : isoDate;
}

function parseAmount(value: TableCell): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const raw = normalizeCell(value);
  if (!raw) return null;

  const negative = raw.includes('(') && raw.includes(')');
  let normalized = raw.replace(/[^\d,.-]/g, '');

  if (normalized.includes(',') && normalized.includes('.')) {
    normalized =
      normalized.lastIndexOf(',') > normalized.lastIndexOf('.')
        ? normalized.replace(/\./g, '').replace(',', '.')
        : normalized.replace(/,/g, '');
  } else if (normalized.includes(',')) {
    normalized = normalized.replace(',', '.');
  }

  const parsed = Number.parseFloat(normalized);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return negative ? -Math.abs(parsed) : parsed;
}

function normalizeTable(table: PdfTable): TableCell[][] {
  return table.map((row) => row.map((cell) => normalizeCell(cell))).filter((row) => row.some((cell) => cell !== ''));
}

function extractPdfTables(result: unknown): PdfTable[] {
  if (!result || typeof result !== 'object') {
    throw new ValidationError('file', 'Failed to read the selected PDF statement');
  }

  const mergedTables = getPdfTableArray((result as { mergedTables?: unknown }).mergedTables);
  if (mergedTables.length > 0) {
    return mergedTables;
  }

  const pages = Array.isArray((result as { pages?: unknown }).pages) ? (result as { pages: unknown[] }).pages : [];
  const pageTables = pages.flatMap((page) =>
    page && typeof page === 'object' ? getPdfTableArray((page as { tables?: unknown }).tables) : [],
  );
  if (pageTables.length === 0) {
    throw new ValidationError('file', 'Failed to read the selected PDF statement');
  }

  return pageTables;
}

function getPdfTableArray(value: unknown): PdfTable[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((table): table is PdfTable => Array.isArray(table) && table.every((row) => Array.isArray(row)));
}

function findHeaderIndex(headers: string[], aliases: readonly string[]): number | null {
  const aliasSet = new Set(aliases.map(normalizeHeader));
  const index = headers.findIndex((header) => aliasSet.has(header));
  return index === -1 ? null : index;
}

function normalizeHeader(value: string): string {
  return value
    .toLowerCase()
    .replace(/[_\s]+/g, ' ')
    .replace(/[^\p{L}\p{N} ]/gu, '')
    .trim();
}

function normalizeCell(value: TableCell): string {
  if (value === null || value === undefined || value === false) {
    return '';
  }
  return String(value)
    .replace(/[\n\r]+/g, ' ')
    .trim();
}

function isSummaryRow(description: string): boolean {
  const normalized = normalizeHeader(description);
  return normalized === 'total' || normalized === 'итого' || normalized === 'разом';
}

function isPdfStatementFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  return fileName.endsWith('.pdf') || file.type === 'application/pdf';
}
